/**
 * Cookie 읽기 유틸 — core-ui 가 BFF 요청 시 *식별자 헤더* 를 자동 합성하기 위해 사용.
 *
 * # SSR safety
 *
 * `typeof document === 'undefined'` 가드로 server runtime 에선 `undefined` 반환.
 * SSR 첫 paint 시점엔 client cookie 를 알 수 없음 — 이건 정상 동작. core-ui 의 widget fetch 는
 * client mount 후 trigger 되므로 SSR HTML 에 영향 없음.
 *
 * # PII 주의
 *
 * cgntId 같은 사용자 식별자는 *값을 로그/에러 메시지/콘솔* 에 노출하지 않는다.
 * 헤더로 BFF 에 전송하는 건 정상 — 로그에 직접 찍거나 console.warn 메시지에 포함하는 것만 금지.
 */

/**
 * `document.cookie` 에서 특정 cookie 의 값을 추출.
 *
 * - SSR / 비-브라우저 환경: `undefined`
 * - cookie 미존재: `undefined`
 * - cookie 빈 값 (`name=`): `undefined` ([^;]+ 매칭 안 됨)
 * - cookie 정상: `decodeURIComponent` 적용된 값
 *
 * 같은 prefix 의 다른 cookie (`cgntId_extra` 등) 와 충돌 안 함 — `name=` 정확 매칭.
 */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  // 정규식 메타 문자 escape — name 에 특수문자가 들어와도 안전
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // (?:^|;\s*) : 문자열 시작 또는 "; " 구분자 뒤에서만 탐색 (중간 substring 오탐 방지)
  // ${escapedName}= : cookie key 를 정확히 일치
  // ([^;]+) : 다음 ';' 전까지의 value 캡처 (빈 문자열은 미매칭 -> undefined 정책)
  const pattern = new RegExp(`(?:^|;\\s*)${escapedName}=([^;]+)`);
  const match = pattern.exec(document.cookie);
  if (match === null) return undefined;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // malformed URI sequence — 원본 그대로 반환
    return match[1];
  }
}
